import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Link as LinkIcon, FileText, Upload, Mail, Phone, Download, Camera, User } from 'lucide-react';
import { toast } from 'sonner';
import api, { resolveFileUrl } from '../utils/api';

const PlayerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    position: '',
    position2: '',
    nationality: '',
    date_of_birth: '',
    current_club: '',
    contract_end_date: '',
    representation_contract_end: '',
    email: '',
    phone: '',
    photo: '',
    salary: '',
    salary_frequency: 'weekly',
    agent_fee: '',
    addons_description: '',
    addons_value: '',
    injury_history: '',
    performance_notes: '',
    preferred_foot: '',
    player_type: '',
    links: [],
    documents: []
  });

  const [newLink, setNewLink] = useState({ name: '', url: '' });
  const [newDocumentName, setNewDocumentName] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetchPlayer();
    }
  }, [id]);

  const fetchPlayer = async () => {
    try {
      const response = await api.get(`/players/${id}`);
      const data = response.data;
      // Auto-calculate age from DOB
      let calculatedAge = data.age || '';
      if (data.date_of_birth) {
        const today = new Date();
        const birth = new Date(data.date_of_birth);
        calculatedAge = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) calculatedAge--;
      }
      setFormData({
        ...data,
        age: String(calculatedAge),
        email: data.email || '',
        phone: data.phone || '',
        photo: data.photo || '',
        links: data.links || [],
        documents: data.documents || []
      });
    } catch (error) {
      toast.error('Failed to load player');
      navigate('/players');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        age: parseInt(formData.age),
        salary: formData.salary ? parseFloat(formData.salary) : null,
        agent_fee: formData.agent_fee ? parseFloat(formData.agent_fee) : null,
        addons_value: formData.addons_value ? parseFloat(formData.addons_value) : null
      };

      if (isEdit) {
        await api.put(`/players/${id}`, payload);
        toast.success('Player updated successfully');
      } else {
        await api.post('/players', payload);
        toast.success('Player created successfully');
      }
      navigate('/players');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save player');
    } finally {
      setLoading(false);
    }
  };

  const addLink = () => {
    if (newLink.name && newLink.url) {
      setFormData({
        ...formData,
        links: [...formData.links, { ...newLink }]
      });
      setNewLink({ name: '', url: '' });
    }
  };

  const removeLink = (index) => {
    setFormData({
      ...formData,
      links: formData.links.filter((_, i) => i !== index)
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const res = await api.post('/upload/document', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const docName = newDocumentName || file.name.replace('.pdf', '');
      
      setFormData({
        ...formData,
        documents: [...formData.documents, { 
          name: docName, 
          filename: file.name,
          url: res.data.url 
        }]
      });
      setNewDocumentName('');
      toast.success(`Document "${docName}" uploaded`);
    } catch (error) {
      toast.error('Failed to upload document');
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeDocument = (index) => {
    setFormData({
      ...formData,
      documents: formData.documents.filter((_, i) => i !== index)
    });
  };

  const downloadDocument = (doc) => {
    if (doc.url) {
      window.open(resolveFileUrl(doc.url), '_blank');
    } else if (doc.data) {
      const link = document.createElement('a');
      link.href = doc.data;
      link.download = doc.filename || `${doc.name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const res = await api.post('/upload/image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, photo: res.data.url });
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    }

    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const removePhoto = () => {
    setFormData({ ...formData, photo: '' });
  };

  const positions = ['GK', 'LB', 'LCB', 'CB', 'RCB', 'RB', 'DM', 'LCM', 'CM', 'RCM', 'AM', 'LW', 'RW', 'CF'];
  const footOptions = ['Left', 'Right', 'Both'];
  const playerTypes = ['Technical', 'Physical', 'Pace', 'Aerial', 'Complete', 'Playmaker', 'Box-to-Box', 'Defensive', 'Creative'];
  const salaryFrequencies = ['weekly', 'monthly', 'yearly'];

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <Link
            to="/players"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Back to Players</span>
          </Link>
          <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
            {isEdit ? 'Edit Player' : 'Add New Player'}
          </h1>
          <p className="text-lg text-zinc-300">Enter player details below</p>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="glass-card p-8 space-y-6">
            {/* Player Photo */}
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                  Player Photo
                </label>
                <div className="relative">
                  {formData.photo ? (
                    <div className="relative group">
                      <img 
                        src={resolveFileUrl(formData.photo)} 
                        alt="Player" 
                        className="w-32 h-32 md:w-40 md:h-40 rounded-lg object-cover border-2 border-white/10"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 md:w-40 md:h-40 rounded-lg bg-zinc-900/50 border-2 border-dashed border-white/20 flex flex-col items-center justify-center">
                      <User className="w-12 h-12 text-zinc-600 mb-2" />
                      <span className="text-xs text-zinc-500">No photo</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={photoInputRef}
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <label
                  htmlFor="photo-upload"
                  className="mt-3 w-full px-4 py-2 bg-white/20 text-white hover:bg-white/30 rounded-md font-bold cursor-pointer flex items-center justify-center gap-2 text-sm"
                >
                  <Camera className="w-4 h-4" />
                  {formData.photo ? 'Change' : 'Upload'}
                </label>
              </div>

              {/* Basic Info Grid */}
              <div className="flex-1">
                <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Basic Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      data-testid="player-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="e.g., Emma Watson"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                      Age
                    </label>
                    <input
                      type="number"
                      readOnly
                      min="15"
                      max="50"
                      data-testid="player-age-input"
                      value={formData.age}
                      className="w-full bg-zinc-900/30 border border-white/10 rounded-md px-4 py-3 text-white/70 outline-none cursor-not-allowed"
                      title="Age is auto-calculated from Date of Birth"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => {
                        const dob = e.target.value;
                        let age = '';
                        if (dob) {
                          const today = new Date();
                          const birth = new Date(dob);
                          age = today.getFullYear() - birth.getFullYear();
                          const m = today.getMonth() - birth.getMonth();
                          if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                        }
                        setFormData({ ...formData, date_of_birth: dob, age: age !== '' ? String(age) : '' });
                      }}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Position 1 *
                  </label>
                  <select
                    required
                    data-testid="player-position-select"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    <option value="">Select position</option>
                    {positions.map((pos) => (
                      <option key={pos} value={pos}>{pos}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Position 2
                  </label>
                  <select
                    data-testid="player-position2-select"
                    value={formData.position2 || ''}
                    onChange={(e) => setFormData({ ...formData, position2: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    <option value="">Select position</option>
                    {positions.map((pos) => (
                      <option key={pos} value={pos}>{pos}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Preferred Foot
                  </label>
                  <select
                    data-testid="player-preferred-foot-select"
                    value={formData.preferred_foot || ''}
                    onChange={(e) => setFormData({ ...formData, preferred_foot: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    <option value="">Select foot</option>
                    {footOptions.map((foot) => (
                      <option key={foot} value={foot}>{foot}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Player Type
                  </label>
                  <select
                    data-testid="player-type-select"
                    value={formData.player_type || ''}
                    onChange={(e) => setFormData({ ...formData, player_type: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white outline-none transition-all duration-200"
                  >
                    <option value="">Select type</option>
                    {playerTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Nationality *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nationality}
                    onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., England"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Current Club
                  </label>
                  <input
                    type="text"
                    value={formData.current_club}
                    onChange={(e) => setFormData({ ...formData, current_club: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., Chelsea FC Women"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Contract End Date
                  </label>
                  <input
                    type="date"
                    value={formData.contract_end_date}
                    onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Representation Contract End Date
                    <span className="text-orange-400 ml-1">(Agency)</span>
                  </label>
                  <input
                    type="date"
                    data-testid="player-representation-end-input"
                    value={formData.representation_contract_end || ''}
                    onChange={(e) => setFormData({ ...formData, representation_contract_end: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-orange-500/20 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Date when our representation contract with this player expires</p>
                </div>
              </div>
            </div>
          </div>

            {/* Contact Details */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6 flex items-center gap-2">
                <Mail className="w-6 h-6 text-blue-400" />
                Contact Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input
                      type="email"
                      data-testid="player-email-input"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="player@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input
                      type="tel"
                      data-testid="player-phone-input"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="+44 7XXX XXX XXX"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contract Details */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Contract Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Salary (£)
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      step="0.01"
                      value={formData.salary}
                      onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                      className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="e.g., 50000"
                    />
                    <select
                      value={formData.salary_frequency || 'weekly'}
                      onChange={(e) => setFormData({ ...formData, salary_frequency: e.target.value })}
                      className="w-32 bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-3 text-white outline-none"
                    >
                      {salaryFrequencies.map((freq) => (
                        <option key={freq} value={freq}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Agent Fee (£)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.agent_fee}
                    onChange={(e) => setFormData({ ...formData, agent_fee: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="e.g., 250000"
                  />
                </div>
              </div>

              {/* Add-ons Section */}
              <div className="mt-6 p-4 bg-white/5 rounded-lg">
                <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-4">Contract Add-ons</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                      Add-ons Description
                    </label>
                    <textarea
                      rows="2"
                      value={formData.addons_description || ''}
                      onChange={(e) => setFormData({ ...formData, addons_description: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="e.g., Goal bonus £5k, Appearance bonus £2k, Title bonus £50k"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                      Total Add-ons Value (£)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.addons_value || ''}
                      onChange={(e) => setFormData({ ...formData, addons_value: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                      placeholder="e.g., 100000"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Links Section */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">
                <LinkIcon className="w-6 h-6 inline mr-2" />
                Website Links
              </h2>
              
              {/* Existing links */}
              {formData.links && formData.links.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.links.map((link, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <LinkIcon className="w-4 h-4 text-blue-400" />
                      <span className="text-white font-medium">{link.name}</span>
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm truncate flex-1">
                        {link.url}
                      </a>
                      <button
                        type="button"
                        onClick={() => removeLink(index)}
                        className="p-1 hover:bg-red-500/20 rounded text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new link */}
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Link name (e.g., Twitter)"
                  value={newLink.name}
                  onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                  className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-2 text-white placeholder:text-zinc-600 outline-none"
                />
                <input
                  type="url"
                  placeholder="URL (https://...)"
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-2 text-white placeholder:text-zinc-600 outline-none"
                />
                <button
                  type="button"
                  onClick={addLink}
                  className="px-4 py-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-md font-bold"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Documents Section - PDF Upload */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">
                <FileText className="w-6 h-6 inline mr-2" />
                PDF Documents
              </h2>
              
              {/* Existing documents */}
              {formData.documents && formData.documents.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <FileText className="w-4 h-4 text-red-400" />
                      <span className="text-white font-medium flex-1">{doc.name}</span>
                      {doc.filename && (
                        <span className="text-xs text-zinc-500">{doc.filename}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="p-1.5 hover:bg-green-500/20 rounded text-green-400"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeDocument(index)}
                        className="p-1.5 hover:bg-red-500/20 rounded text-red-400"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload new document */}
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  placeholder="Document name (e.g., Contract)"
                  value={newDocumentName}
                  onChange={(e) => setNewDocumentName(e.target.value)}
                  className="flex-1 bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-2 text-white placeholder:text-zinc-600 outline-none"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="pdf-upload"
                />
                <label
                  htmlFor="pdf-upload"
                  className="px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-md font-bold cursor-pointer flex items-center gap-2"
                >
                  <Upload className="w-5 h-5" />
                  Upload PDF
                </label>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Upload PDF documents (contracts, medical reports, etc.) - Max 10MB</p>
            </div>

            {/* Additional Info */}
            <div className="pt-6 border-t border-white/5">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6">Additional Information</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows="4"
                    value={formData.performance_notes}
                    onChange={(e) => setFormData({ ...formData, performance_notes: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="Any notes about player strengths, weaknesses, personality..."
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Injury History
                  </label>
                  <textarea
                    rows="3"
                    value={formData.injury_history}
                    onChange={(e) => setFormData({ ...formData, injury_history: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
                    placeholder="Any past injuries or health concerns..."
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-white/5 flex gap-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="save-player-button"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" strokeWidth={2} />
                {loading ? 'Saving...' : isEdit ? 'Update Player' : 'Create Player'}
              </button>
              <Link
                to="/players"
                className="px-6 py-3 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm transition-all duration-200 uppercase tracking-wide font-bold"
              >
                Cancel
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlayerForm;
